import React, { useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useOptionalAuth } from 'contexts/AuthContext';

type Props = {
  password: string;
  language?: string;
  /** trueの場合、全ユーザーにパスワードを表示する（コピペ用途の完成例向け） */
  showPassword?: boolean;
  children: string;
};

/**
 * パスワード保護付きコードブロック
 * - 通常ユーザー: パスワード入力で解除
 * - DEVELOPER権限: パスワード不要で直接閲覧可能 + パスワードコピーボタン表示
 * - showPassword: 全ユーザーにパスワード表示（コピペ用途）
 */
const WorkflowLockedCodeBlock: React.FC<Props> = ({
  password,
  language = 'markdown',
  showPassword = false,
  children,
}) => {
  // devdocs はゲスト閲覧可（OptionalAuth 配下）のため useOptionalAuth を使う。
  // ゲスト（userSession=null）は非 DEVELOPER 扱い。
  const { userSession } = useOptionalAuth();
  const isDeveloper = showPassword || (userSession?.roles.includes('DEVELOPER') ?? false);

  const [unlocked, setUnlocked] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inputPassword, setInputPassword] = useState('');
  const [error, setError] = useState(false);

  const handleUnlock = () => {
    if (inputPassword === password) {
      setUnlocked(true);
      setDialogOpen(false);
      setError(false);
    } else {
      setError(true);
    }
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(password);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(children);
  };

  // DEVELOPER向け: パスワード表示 + 直接開くボタン
  if (isDeveloper && !unlocked) {
    return (
      <Box
        sx={{
          border: 1,
          borderColor: '#2d333b',
          borderRadius: 2,
          p: 2.5,
          bgcolor: '#0d1117',
        }}
      >
        <Stack spacing={1.5} alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ color: '#768390' }}>
              パスワード:
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: '#e6edf3', fontFamily: 'monospace' }}
            >
              {password}
            </Typography>
            <IconButton size="small" onClick={handleCopyPassword} sx={{ p: 0.5, color: '#768390' }}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
          <Button
            size="small"
            startIcon={<LockOpenIcon sx={{ fontSize: 16 }} />}
            onClick={() => setUnlocked(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              color: '#90caf9',
            }}
          >
            完成例を見る
          </Button>
        </Stack>
      </Box>
    );
  }

  // ロック状態
  if (!unlocked) {
    return (
      <>
        <Box
          sx={{
            border: 1,
            borderColor: '#2d333b',
            borderRadius: 2,
            p: 3,
            bgcolor: '#0d1117',
            textAlign: 'center',
          }}
        >
          <Stack spacing={1.5} alignItems="center">
            <LockIcon sx={{ fontSize: 28, color: '#768390' }} />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDialogOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                borderColor: '#30363d',
                color: '#90caf9',
                '&:hover': { borderColor: '#90caf9' },
              }}
            >
              完成例を見る
            </Button>
          </Stack>
        </Box>

        {/* パスワード入力ダイアログ */}
        <Dialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setError(false);
            setInputPassword('');
          }}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            elevation: 0,
            sx: { borderRadius: 3, border: 1, borderColor: 'divider' },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              完成例を表示する
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                本当に自分で考え尽くしましたか？
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                完成例を見る前に、もう一度自分のアウトプットを振り返ってみてください。
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                「ここまでは書けたけど、ここが分からない」——それを言語化できるなら、あなたはもう十分に考えています。
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                そう思えたなら、担当者にパスワードを聞いてください。
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  placeholder="パスワード"
                  value={inputPassword}
                  onChange={(e) => {
                    setInputPassword(e.target.value);
                    setError(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleUnlock();
                  }}
                  error={error}
                  helperText={error ? 'パスワードが正しくありません' : undefined}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUnlock}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                  }}
                >
                  開く
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // アンロック状態 — コードを表示
  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: 1,
        borderColor: '#2d333b',
      }}
    >
      {/* ヘッダー */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5,
          py: 0.75,
          bgcolor: '#1c2128',
          borderBottom: 1,
          borderColor: '#2d333b',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <LockOpenIcon sx={{ fontSize: 14, color: '#4caf50' }} />
          <Typography variant="caption" fontWeight={600} sx={{ color: '#90caf9' }}>
            完成例
          </Typography>
          {showPassword && (
            <>
              <Typography variant="caption" sx={{ color: '#768390' }}>
                |
              </Typography>
              <Typography variant="caption" sx={{ color: '#768390' }}>
                パスワード:
              </Typography>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: '#e6edf3', fontFamily: 'monospace' }}
              >
                {password}
              </Typography>
              <IconButton
                size="small"
                onClick={handleCopyPassword}
                sx={{ p: 0.5, color: '#768390' }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </>
          )}
          {language && !showPassword && (
            <Typography variant="caption" sx={{ color: '#768390' }}>
              {language}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {isDeveloper && (
            <Button
              size="small"
              startIcon={<LockIcon sx={{ fontSize: 14 }} />}
              onClick={() => setUnlocked(false)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.7rem',
                color: '#768390',
                minWidth: 'auto',
                px: 1,
                py: 0.25,
              }}
            >
              閉じる
            </Button>
          )}
          <IconButton size="small" onClick={handleCopyCode} sx={{ p: 0.5, color: '#768390' }}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* コード本体 */}
      <Box
        sx={{
          p: 1.5,
          overflow: 'auto',
          bgcolor: '#0d1117',
          color: '#e6edf3',
          fontSize: '0.78rem',
          lineHeight: 1.6,
          fontFamily: '"JetBrains Mono", "Fira Code", "SFMono-Regular", Consolas, monospace',
          whiteSpace: 'pre',
          m: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default WorkflowLockedCodeBlock;
